import { z } from "zod";
import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import useTitanApp from "../../hooks/useTitanApp";

// import type { Subscription } from "./subscriptionsData";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format a date to YYYY-MM-DD format for HTML date inputs
 * Handles string dates, Date objects, and invalid dates gracefully
 */
const formatDateForInput = (date: string | Date | undefined): string => {
  if (!date) return "";
  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return "";
    return dateObj.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type GoalStatus = "pending" | "in-progress" | "achieved" | "abandoned";
export type VitalType =
  | "weight"
  | "blood-pressure"
  | "heart-rate"
  | "temperature"
  | "height"
  | "bmi"
  | "oxygen-saturation"
  | "blood-glucose";
export type TargetDirection = "AtOrAbove" | "AtOrBelow" | "EqualTo";
type TrackingFrequency = "daily" | "weekly" | "bi-weekly" | "monthly";
export type BillingFrequency =
  | "weekly"
  | "bi-weekly"
  | "monthly"
  | "quarterly"
  | "yearly";
export type TreatmentPlanStatus =
  | "Active"
  | "Completed"
  | "Paused"
  | "Cancelled";

export interface TreatmentGoal {
  id: string;
  description: string;
  linkedVitalType?: VitalType;
  targetDirection?: TargetDirection;
  targetValue: string;
  targetDate: string;
  status: GoalStatus;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
}

interface SubscriptionPlan {
  id: string;
  linkedPrescriptionId: string;
  prescriptionName?: string;
  billingFrequency: BillingFrequency;
  pricePerSupply: number;
  totalSupplies: number;
  currentSupply: number;
  paymentStatus: "active" | "pending" | "overdue" | "cancelled";
  nextBillingDate: string;
  startDate: string;
  autoRenew: boolean;
}

interface ConsultationHistoryEntry {
  id: string;
  date: string;
  time: string;
  type: string;
  pharmacist: string;
  summary?: string;
}

export interface TreatmentPlan {
  id: string;
  name: string;
  description?: string;
  consultationId?: string;
  patientId: string;
  patientName?: string;
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
  startDate: string;
  endDate?: string;
  status: TreatmentPlanStatus;
  goals: TreatmentGoal[];
  followUpAppointments: TreatmentFollowUpAppointment[];
  linkedPrescriptions: string[]; // Prescription IDs
  subscriptionPlan?: SubscriptionPlan;
  consultationHistory?: ConsultationHistoryEntry[];
  treatmentPlanConfigurationId?: string; // ID of the Settings template this plan was created from
}

// ── ADD this new interface (matches Events C# class + BookingForm) ──────────
export interface TreatmentFollowUpAppointment {
  id?: string;
  name?: string; // configured appointment name from treatment plan configuration
  patientName?: string;
  patientId?: string;
  patientID?: string;
  pharmacist?: string; // display name
  resourceId?: string; // pharmacist ID
  serviceType?: string; // service display name
  serviceId?: string; // service ID
  serviceID?: string;
  date?: string; // YYYY-MM-DD
  time?: string; // HH:MM
  intervalValue?: string | number;
  intervalUnit?: "Days" | "Weeks" | "Months";
  duration?: string | number;
  durationUnit?: string;
  room?: string; // room display name
  roomId?: string;
  notes?: string;
  email?: string;
  mobile?: string;
  confirmationMethod?: string;
  tasksToComplete?: string[];
  vaccinesAdminister?: any[];
  preConsultationTemplateId?: string | null;
  consultationId?: string;
  consultationID?: string;
  startDate?: string; // ISO — derived from date+time for API
  endDate?: string; // ISO — derived from date+time+duration
  eventStatus?: number;
  preSelectedBySettings?: boolean;
  preSelectedIntervalLabel?: string;
  isDateManuallySet?: boolean;
}

// ============================================================================
// ZOD SCHEMAS FOR VALIDATION
// ============================================================================

export const treatmentGoalSchema = z.object({
  description: z
    .string()
    .min(3, "Description must be at least 3 characters")
    .max(500, "Description must be less than 500 characters"),
  linkedVitalType: z
    .enum([
      "weight",
      "blood-pressure",
      "heart-rate",
      "temperature",
      "height",
      "bmi",
      "oxygen-saturation",
      "blood-glucose",
    ])
    .optional(),
  targetDirection: z.enum(["AtOrAbove", "AtOrBelow", "EqualTo"]).optional(),
  targetValue: z.string().min(1, "Target value is required"),
  targetDate: z.string().min(1, "Target date is required"),
  status: z.enum(["pending", "in-progress", "achieved", "abandoned"]),
  notes: z.string().optional(),
});

export const subscriptionPlanSchema = z.object({
  linkedPrescriptionId: z.string().min(1, "Please select a prescription"),
  billingFrequency: z.enum([
    "weekly",
    "bi-weekly",
    "monthly",
    "quarterly",
    "yearly",
  ]),
  pricePerSupply: z.number().min(0, "Price must be positive"),
  totalSupplies: z.number().min(1, "Total supplies must be at least 1"),
  autoRenew: z.boolean().default(true),
});

export const treatmentPlanSchema = z.object({
  name: z
    .string()
    .min(3, "Plan name must be at least 3 characters")
    .max(200, "Plan name must be less than 200 characters"),
  description: z
    .string()
    .max(1000)
    .optional(),
  startDate: z.string().min(1, "Start date is required"),
  endDate: z.string().optional(),
  status: z
    .enum(["Active", "Completed", "Paused", "Cancelled"])
    .default("Active"),
});

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const getGoalStatusColor = (status: GoalStatus): string => {
  switch (status) {
    case "achieved":
      return "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
    case "in-progress":
      return "bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300";
    case "pending":
      return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
    case "abandoned":
      return "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
  }
};

export const getGoalStatusLabel = (status: GoalStatus): string => {
  switch (status) {
    case "achieved":
      return "Achieved";
    case "in-progress":
      return "In Progress";
    case "pending":
      return "Pending";
    case "abandoned":
      return "Abandoned";
    default:
      return status;
  }
};

export const getVitalTypeLabel = (vitalType: VitalType): string => {
  switch (vitalType) {
    case "weight":
      return "Weight";
    case "blood-pressure":
      return "Blood Pressure";
    case "heart-rate":
      return "Heart Rate";
    case "temperature":
      return "Temperature";
    case "height":
      return "Height";
    case "bmi":
      return "BMI";
    case "oxygen-saturation":
      return "Oxygen Saturation";
    case "blood-glucose":
      return "Blood Glucose";
    default:
      return vitalType;
  }
};

export const getVitalTypeUnit = (vitalType: VitalType): string => {
  switch (vitalType) {
    case "weight":
      return "kg";
    case "blood-pressure":
      return "mmHg";
    case "heart-rate":
      return "BPM";
    case "temperature":
      return "°C";
    case "height":
      return "cm";
    case "bmi":
      return "";
    case "oxygen-saturation":
      return "%";
    case "blood-glucose":
      return "mmol/L";
    default:
      return "";
  }
};

export const getFrequencyLabel = (frequency: TrackingFrequency): string => {
  switch (frequency) {
    case "daily":
      return "Daily";
    case "weekly":
      return "Weekly";
    case "bi-weekly":
      return "Bi-weekly";
    case "monthly":
      return "Monthly";
    default:
      return frequency;
  }
};

export const getBillingFrequencyLabel = (
  frequency: BillingFrequency,
): string => {
  switch (frequency) {
    case "weekly":
      return "Weekly";
    case "bi-weekly":
      return "Bi-weekly";
    case "monthly":
      return "Monthly";
    case "quarterly":
      return "Quarterly";
    case "yearly":
      return "Yearly";
    default:
      return frequency;
  }
};

export const calculateNextBillingDate = (
  startDate: string,
  frequency: BillingFrequency,
  currentSupply: number,
): string => {
  const start = new Date(startDate);
  let daysToAdd = 0;

  switch (frequency) {
    case "weekly":
      daysToAdd = 7 * currentSupply;
      break;
    case "bi-weekly":
      daysToAdd = 14 * currentSupply;
      break;
    case "monthly":
      daysToAdd = 30 * currentSupply;
      break;
    case "quarterly":
      daysToAdd = 90 * currentSupply;
      break;
    case "yearly":
      daysToAdd = 365 * currentSupply;
      break;
  }

  const nextDate = new Date(start);
  nextDate.setDate(nextDate.getDate() + daysToAdd);
  return nextDate.toISOString().split("T")[0];
};

export const getTargetDirectionSymbol = (
  direction?: TargetDirection,
): string => {
  switch (direction) {
    case "AtOrAbove":
      return "≥";
    case "AtOrBelow":
      return "≤";
    case "EqualTo":
      return "=";
    default:
      return "";
  }
};

export const getTargetDirectionLabel = (
  direction?: TargetDirection,
): string => {
  switch (direction) {
    case "AtOrAbove":
      return "At or above";
    case "AtOrBelow":
      return "At or below";
    case "EqualTo":
      return "Equal to";
    default:
      return "";
  }
};

export const calculateTotalCost = (
  pricePerSupply: number,
  totalSupplies: number,
): number => {
  return pricePerSupply * totalSupplies;
};

// ============================================================================
// DEFAULT DATA
// ============================================================================

export const getDefaultGoal = (): Omit<TreatmentGoal, "id" | "createdAt"> => ({
  description: "",
  linkedVitalType: undefined,
  targetValue: "",
  targetDate: "",
  status: "pending",
  notes: "",
});

export const getDefaultSubscriptionPlan = (): Omit<
  SubscriptionPlan,
  "id" | "currentSupply" | "paymentStatus" | "nextBillingDate"
> => ({
  linkedPrescriptionId: "",
  prescriptionName: "",
  billingFrequency: "monthly",
  pricePerSupply: 0,
  totalSupplies: 12,
  startDate: new Date().toISOString().split("T")[0],
  autoRenew: true,
});

export const getDefaultTreatmentPlan = (): Omit<
  TreatmentPlan,
  "id" | "createdAt" | "patientId" | "createdBy"
> => ({
  name: "",
  description: "",
  startDate: new Date().toISOString().split("T")[0],
  status: "Active",
  goals: [],
  followUpAppointments: [],
  linkedPrescriptions: [],
});

// ============================================================================
// CUSTOM HOOK FOR FORM STATE
// ============================================================================

interface TreatmentPlanFormData {
  // Step 1: Overview
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  status: TreatmentPlanStatus;
  treatmentPlanConfigurationId?: string;

  // Step 2: Goals
  goals: TreatmentGoal[];

  // Step 3: Follow-up Appointments
  followUpAppointments: TreatmentFollowUpAppointment[];

  // Step 4: Linked Prescriptions
  linkedPrescriptions: string[];

  // Step 5: Subscription Plan
  subscriptionPlan?: SubscriptionPlan;
}

export const useTreatmentPlanFormState = (existingPlan?: TreatmentPlan) => {
  const prevPlanIdRef = useRef<string | undefined>(undefined);
  const [currentStep, setCurrentStep] = useState(1);
  const {
    appState,
    scheduleEventState,
    pharmacyServiceState,
    patientState,
  } = useTitanApp();
  const { consultationData } = patientState || {};

  const { darkMode, currentPharmacyId } = appState || {};
  const [formData, setFormData] = useState<Partial<TreatmentPlanFormData>>(
    existingPlan
      ? {
          name: existingPlan.name || "",
          description: existingPlan.description || "",
          startDate: formatDateForInput(existingPlan.startDate),
          endDate: formatDateForInput(existingPlan.endDate),
          status: existingPlan.status || "Active",
          treatmentPlanConfigurationId:
            (existingPlan as any)?.treatmentPlanConfigurationId || "",
          goals: existingPlan.goals || [],
          followUpAppointments: existingPlan.followUpAppointments || [],
          linkedPrescriptions: existingPlan.linkedPrescriptions || [],
          subscriptionPlan: existingPlan.subscriptionPlan,
        }
      : {
          name: "",
          description: "",
          startDate: new Date().toISOString().split("T")[0],
          endDate: "",
          status: "Active",
          treatmentPlanConfigurationId: "",
          goals: [],
          followUpAppointments: [],
          linkedPrescriptions: [],
        },
  );
  useEffect(() => {
    if (!existingPlan) return;

    // Only reinitialize when we switch to a genuinely different plan
    const incomingId = (existingPlan as any).treatmentPlanId || existingPlan.id;

    if (incomingId === prevPlanIdRef.current) return; // same plan → skip reset
    prevPlanIdRef.current = incomingId;

    setFormData({
      name: existingPlan.name || "",
      description: existingPlan.description || "",
      startDate: formatDateForInput(existingPlan.startDate),
      endDate: formatDateForInput(existingPlan.endDate),
      status: existingPlan.status || "Active",
      treatmentPlanConfigurationId:
        (existingPlan as any)?.treatmentPlanConfigurationId || "",
      goals: existingPlan.goals || [],
      followUpAppointments: existingPlan.followUpAppointments || [],
      linkedPrescriptions: existingPlan.linkedPrescriptions || [],
      subscriptionPlan: existingPlan.subscriptionPlan,
    });
  }, [existingPlan]);

  const updateFormData = (updates: Partial<TreatmentPlanFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const addGoal = (goal: TreatmentGoal) => {
    setFormData((prev) => ({
      ...prev,
      goals: [...(prev.goals || []), goal],
    }));
  };

  const updateGoal = (id: string, updates: Partial<TreatmentGoal>) => {
    setFormData((prev) => ({
      ...prev,
      goals: (prev.goals || []).map((g) =>
        g.id === id ? { ...g, ...updates } : g,
      ),
    }));
  };

  const removeGoal = (id: string) => {
    setFormData((prev) => ({
      ...prev,
      goals: (prev.goals || []).filter((g) => g.id !== id),
    }));
  };

  const addFollowUpAppointment = (
    appointment: TreatmentFollowUpAppointment,
  ) => {
    setFormData((prev) => ({
      ...prev,
      followUpAppointments: [...(prev.followUpAppointments || []), appointment],
    }));
  };

  const removeFollowUpAppointment = (appointmentId: string) => {
    setFormData((prev) => ({
      ...prev,
      followUpAppointments: (prev.followUpAppointments || []).filter(
        (appt) => (appt.id ?? appt.serviceId) !== appointmentId,
      ),
    }));
  };

  const addLinkedPrescription = (prescriptionId: string) => {
    setFormData((prev) => ({
      ...prev,
      linkedPrescriptions: [
        ...(prev.linkedPrescriptions || []),
        prescriptionId,
      ],
    }));
  };

  const removeLinkedPrescription = (prescriptionId: string) => {
    setFormData((prev) => ({
      ...prev,
      linkedPrescriptions: (prev.linkedPrescriptions || []).filter(
        (id) => id !== prescriptionId,
      ),
    }));
  };

  const setSubscriptionPlan = (plan?: SubscriptionPlan) => {
    setFormData((prev) => ({
      ...prev,
      subscriptionPlan: plan,
    }));
  };

  const canGoNext = (): boolean => {
    switch (currentStep) {
      case 1: // Overview
        return !!(formData.name && formData.startDate);
      case 2: // Goals - optional
        return true;
      case 3: // Appointments - optional
        return true;
      case 4: // Prescriptions - optional
        return true;
      case 5: // Vitals - optional
        return true;
      case 6: // Subscription - optional
        return true;
      case 7: // Review
        return true;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (canGoNext() && currentStep < 7) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const goToStep = (step: number) => {
    if (step >= 1 && step <= 7) {
      setCurrentStep(step);
    }
  };

  const reset = () => {
    setCurrentStep(1);
    setFormData({
      name: "",
      description: "",
      startDate: new Date().toISOString().split("T")[0],
      endDate: "",
      status: "Active",
      treatmentPlanConfigurationId: "",
      goals: [],
      followUpAppointments: [],
      linkedPrescriptions: [],
    });
  };

  const updateFollowUpAppointment = (
    appointmentId: string,
    updates: Partial<TreatmentFollowUpAppointment>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      followUpAppointments: (prev.followUpAppointments || []).map((appt) =>
        (appt.id ?? appt.serviceId) === appointmentId
          ? { ...appt, ...updates }
          : appt,
      ),
    }));
  };

  return {
    currentStep,
    formData,
    updateFormData,
    addGoal,
    updateGoal,
    removeGoal,
    addFollowUpAppointment,
    removeFollowUpAppointment,
    addLinkedPrescription,
    removeLinkedPrescription,
    setSubscriptionPlan,
    canGoNext,
    nextStep,
    prevStep,
    goToStep,
    reset,
    updateFollowUpAppointment,
    currentPharmacyId,
    consultationData,
  };
};

// ============================================================================
// SUBSCRIPTION CONVERSION HELPER
// ============================================================================

/**
 * Converts a SubscriptionPlan (embedded in a TreatmentPlan) into a full Subscription record
 * for use in patient subscription lists and management views.
 *
 * @param plan - The SubscriptionPlan from the treatment plan
 * @param treatmentPlan - The parent TreatmentPlan
 * @param patient - Patient information for the subscription
 * @returns A complete Subscription object with all required fields
 */
// export const convertSubscriptionPlanToSubscription = (
//   plan: SubscriptionPlan,
//   treatmentPlan: TreatmentPlan,
//   patient: {
//     id: string;
//     name: string;
//     nhsNumber: string;
//   }
// ): Subscription => {
//   // Map billing frequency to repeat period format
//   const getRepeatPeriod = (frequency: BillingFrequency): string => {
//     switch (frequency) {
//       case "weekly":
//         return "Every week";
//       case "bi-weekly":
//         return "Every 2 weeks";
//       case "monthly":
//         return "Every month";
//       case "quarterly":
//         return "Every 3 months";
//       case "yearly":
//         return "Every year";
//       default:
//         return "Every month";
//     }
//   };

//   // Map payment status to subscription status
//   const getSubscriptionStatus = (
//     paymentStatus: SubscriptionPlan["paymentStatus"]
//   ): Subscription["status"] => {
//     switch (paymentStatus) {
//       case "active":
//         return "supplied";
//       case "pending":
//         return "awaiting_payment";
//       case "overdue":
//         return "overdue";
//       case "cancelled":
//         return "consultation_required";
//       default:
//         return "supplied";
//     }
//   };

//   // Calculate last supply date (approximate based on next billing date and frequency)
//   const calculateLastSupplyDate = (nextDate: string, frequency: BillingFrequency): string => {
//     const next = new Date(nextDate);
//     const daysToSubtract = frequency === "weekly" ? 7
//       : frequency === "bi-weekly" ? 14
//       : frequency === "monthly" ? 30
//       : frequency === "quarterly" ? 90
//       : 365;

//     const lastDate = new Date(next);
//     lastDate.setDate(lastDate.getDate() - daysToSubtract);
//     return lastDate.toISOString();
//   };

//   // Generate patient initials
//   const getInitials = (name: string): string => {
//     return name
//       .split(" ")
//       .map((part) => part[0])
//       .join("")
//       .toUpperCase();
//   };

//   return {
//     id: plan.id,
//     patientName: patient.name,
//     patientInitials: getInitials(patient.name),
//     patientNHS: patient.nhsNumber,
//     medication: plan.prescriptionName || "Unknown Medication",
//     prescribedBy: treatmentPlan.createdBy || "Unknown Prescriber",
//     currentIssue: plan.currentSupply,
//     totalIssues: plan.totalSupplies,
//     lastSupplyDate: calculateLastSupplyDate(plan.nextBillingDate, plan.billingFrequency),
//     nextSupplyDate: plan.nextBillingDate,
//     repeatPeriod: getRepeatPeriod(plan.billingFrequency),
//     status: getSubscriptionStatus(plan.paymentStatus),
//     serviceType: "pharmacy-first", // Default service type
//     consultationDate: treatmentPlan.createdAt.split("T")[0],
//     consultationTime: new Date(treatmentPlan.createdAt).toLocaleTimeString("en-GB", {
//       hour: "2-digit",
//       minute: "2-digit",
//     }),
//     dosage: "", // This would need to be pulled from the linked prescription
//     quantity: "", // This would need to be pulled from the linked prescription
//     priority: plan.paymentStatus === "overdue" ? "urgent" : "normal",
//     pricePerIssue: plan.pricePerSupply,
//     issueHistory: [], // Would be populated from payment/dispensing records
//     treatmentPlanId: treatmentPlan.id,
//     treatmentPlanName: treatmentPlan.name,
//   };
// };

// ============================================================================
// GOAL ACHIEVEMENT AUTOMATION
// ============================================================================

/**
 * Checks if a goal's target has been achieved based on a new vital reading
 *
 * @param goal - The treatment goal to check
 * @param vitalValue - The value from the vital reading
 * @returns true if the goal target has been achieved
 */
const isGoalAchieved = (goal: TreatmentGoal, vitalValue: string): boolean => {
  if (!goal.linkedVitalType || !goal.targetValue) {
    return false;
  }

  // For blood pressure, we need special handling
  if (goal.linkedVitalType === "blood-pressure") {
    // Parse systolic/diastolic from both strings (e.g., "120/80")
    const parseBloodPressure = (
      bp: string,
    ): { systolic: number; diastolic: number } | null => {
      const match = bp.match(/(\d+)\/(\d+)/);
      if (!match) return null;
      return {
        systolic: parseInt(match[1]),
        diastolic: parseInt(match[2]),
      };
    };

    const targetBP = parseBloodPressure(goal.targetValue);
    const actualBP = parseBloodPressure(vitalValue);

    if (!targetBP || !actualBP) return false;

    // For blood pressure goals, we typically want the value to be AT or BELOW the target
    // (e.g., target 120/80 means systolic should be <= 120 AND diastolic <= 80)
    return (
      actualBP.systolic <= targetBP.systolic &&
      actualBP.diastolic <= targetBP.diastolic
    );
  }

  // For numeric vitals (weight, BMI, glucose, etc.)
  const targetNum = parseFloat(goal.targetValue);
  const actualNum = parseFloat(vitalValue);

  if (isNaN(targetNum) || isNaN(actualNum)) {
    // If we can't parse as numbers, do string comparison
    return vitalValue.toLowerCase() === goal.targetValue.toLowerCase();
  }

  // Determine goal direction based on vital type
  // For most vitals, we want to reach or go below the target (weight loss, lower BP, etc.)
  // But for some vitals like oxygen saturation, we might want to be at or above
  const higherIsBetter = ["oxygen-saturation", "height"].includes(
    goal.linkedVitalType,
  );

  if (higherIsBetter) {
    return actualNum >= targetNum;
  } else {
    // For weight, BMI, blood glucose, heart rate, temperature - lower is typically better
    return actualNum <= targetNum;
  }
};

/**
 * Automatically checks and updates treatment plan goals when a new vital is recorded
 *
 * @param vitalType - The type of vital that was recorded
 * @param vitalValue - The value of the vital reading
 * @param treatmentPlans - Array of treatment plans to check
 * @returns Updated treatment plans with goal statuses adjusted
 */
export const checkAndUpdateGoalsForVital = (
  vitalType: VitalType,
  vitalValue: string,
  treatmentPlans: TreatmentPlan[],
): TreatmentPlan[] => {
  let hasChanges = false;

  const updatedPlans = treatmentPlans.map((plan) => {
    // Only check active plans
    if (plan.status !== "Active") {
      return plan;
    }

    let planChanged = false;

    // Check each goal in the plan
    const updatedGoals = plan.goals.map((goal) => {
      // Skip if this goal is not linked to the vital type we just recorded
      if (goal.linkedVitalType !== vitalType) {
        return goal;
      }

      // Skip if goal is already achieved or abandoned
      if (goal.status === "achieved" || goal.status === "abandoned") {
        return goal;
      }

      // Check if the goal target has been achieved
      const achieved = isGoalAchieved(goal, vitalValue);
      const newStatus = achieved
        ? ("achieved" as GoalStatus)
        : ("in-progress" as GoalStatus);

      // Only update if status actually changed
      if (newStatus !== goal.status) {
        planChanged = true;
        hasChanges = true;
        return {
          ...goal,
          status: newStatus,
          updatedAt: new Date().toISOString(),
        };
      }

      return goal;
    });

    // Only create new plan object if goals actually changed
    if (planChanged) {
      return {
        ...plan,
        goals: updatedGoals,
        updatedAt: new Date().toISOString(),
      };
    }

    return plan;
  });

  // Return original array if nothing changed to prevent unnecessary re-renders
  return hasChanges ? updatedPlans : treatmentPlans;
};
